import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import AOS from 'aos';
import 'aos/dist/aos.css';

const AnnualDinner = () => {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  useEffect(() => {
    window.scrollTo(0, 0);
    AOS.init({ duration: 1000 });

    const targetDate = new Date("February 21, 2026 16:00:00").getTime();
    const timer = setInterval(() => {
      const now = new Date().getTime();
      const distance = targetDate - now;
      if (distance < 0) {
        clearInterval(timer);
      } else {
        setTimeLeft({
          days: Math.floor(distance / (1000 * 60 * 60 * 24)),
          hours: Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
          minutes: Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
          seconds: Math.floor((distance % (1000 * 60)) / 1000),
        });
      }
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const timelineItems = [
    { time: "4:00 PM", title: "Round Table Conversation", desc: "Open dialogue with community leaders." },
    { time: "4:45 PM", title: "Presentation Session", desc: "Keynote address and organization updates." },
    { time: "5:30 PM", title: "Meditation Session", desc: "A moment of reflection and spiritual unity." },
    { time: "6:00 PM", title: "Dinner", desc: "Catered meal shared in fellowship." },
    { time: "6:30 PM", title: "Meet Your Candidates", desc: "Engage with local political candidates." }
  ];

  const speakers = [
    { name: "Speaker Name 1", role: "Interfaith Scholar", img: "/staff1.jpg" },
    { name: "Speaker Name 2", role: "Community Activist", img: "/staff1.jpg" },
    { name: "Speaker Name 3", role: "Guest Representative", img: "/staff1.jpg" }
  ];

  return (
    <div className="min-h-screen bg-white font-sans overflow-x-hidden">

      {/* 1. HERO SECTION */}
      <section
        className="relative h-[45vh] flex items-center justify-center bg-center"
        style={{ backgroundImage: `linear-gradient(rgba(0,0,0,0.65), rgba(0,0,0,0.65)), url('/communitydinner.jpg')` }}
      >
        <div className="text-center text-white px-4">
          <motion.h1
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className="text-4xl md:text-6xl font-serif font-black uppercase tracking-tight"
          >
            Annual Dinner 2026
          </motion.h1>
          <div className="h-1.5 w-20 bg-[#f9b50b] mx-auto mt-4"></div>

          <p className="mt-4 uppercase tracking-[0.3em] text-xs font-bold">
            <Link to="/" className="hover:text-[#f9b50b] text-gray-300 transition-colors">Home</Link>
            <span className="mx-2 text-gray-400">/</span>
            <span className="text-white">Events</span>
          </p>
        </div>
      </section>

      {/* 2. MAIN CONTENT WRAPPER - mt-16 added for space after Hero */}
      <div className="max-w-6xl mx-auto px-6 py-16 mt-20">

        {/* SUB-HEADER & COUNTDOWN */}
        <div className="text-center mb-12" data-aos="fade-up">
          <h2 className="text-3xl md:text-5xl font-serif font-bold text-[#221f1f] uppercase leading-tight mb-4">
            Celebrating <span className="text-[#f9b50b]">Unity & Dialogue</span>
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-sm md:text-base italic">
            Join us for an evening of reflection, shared meals, and community building as we honor our commitment to interfaith harmony.
          </p>
        </div>

        {/* COUNTDOWN - mb-32 added for large space before Timeline */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-40" data-aos="zoom-in">
          {[{ label: "Days", value: timeLeft.days }, { label: "Hours", value: timeLeft.hours }, { label: "Minutes", value: timeLeft.minutes }, { label: "Seconds", value: timeLeft.seconds }].map((unit, idx) => (
            <div key={idx} className="bg-[#221f1f] p-6 text-center rounded-xl border-b-4 border-[#f9b50b] shadow-xl">
              <span className="block text-4xl md:text-5xl font-bold text-[#f9b50b]">{unit.value}</span>
              <span className="text-white text-xs uppercase font-bold tracking-widest">{unit.label}</span>
            </div>
          ))}
        </div>

        {/* TIMELINE SECTION */}
        <div className="mb-32">
          <h2 className="text-3xl font-serif font-bold uppercase text-center mb-16 flex items-center justify-center gap-3 text-[#221f1f]">
            <span className="h-1 w-10 bg-[#f9b50b]"></span> Event Timeline <span className="h-1 w-10 bg-[#f9b50b]"></span>
          </h2>

          <div className="relative">
            <div className="hidden md:block absolute top-1/2 left-0 w-full h-1 bg-gray-100 -translate-y-8"></div>

            <div className="relative grid grid-cols-1 md:grid-cols-5 gap-8">
              {timelineItems.map((item, idx) => (
                <div key={idx} className="relative flex md:flex-col items-start md:items-center group" data-aos="fade-up" data-aos-delay={idx * 150}>
                  {idx !== timelineItems.length - 1 && (
                    <div className="absolute left-[19px] top-10 w-1 h-full bg-gray-100 md:hidden"></div>
                  )}
                  <div className="z-10 w-10 h-10 bg-white border-4 border-[#f9b50b] rounded-full flex items-center justify-center group-hover:bg-[#f9b50b] transition-colors duration-300">
                    <div className="w-2 h-2 bg-[#221f1f] rounded-full"></div>
                  </div>
                  <div className="ml-6 md:ml-0 md:mt-6 md:text-center">
                    <span className="inline-block bg-[#f9b50b] text-[#221f1f] text-[10px] font-bold px-2 py-1 rounded mb-2">
                      {item.time}
                    </span>
                    <h4 className="font-bold text-[#221f1f] uppercase text-sm leading-tight">{item.title}</h4>
                    <p className="text-gray-500 text-xs mt-2 italic max-w-[150px] mx-auto">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* SPEAKERS SECTION */}
        {/* <div className="mb-24 bg-[#f9f9f9] py-16 px-8 rounded-3xl border border-gray-100 shadow-sm">
          <h2 className="text-3xl font-serif font-bold uppercase text-center mb-12 text-[#221f1f]">
            Meet Our Speakers
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {speakers.map((s, i) => (
              <div key={i} className="text-center group" data-aos="fade-up">
                <div className="relative w-48 h-48 mx-auto mb-6">
                  <div className="absolute inset-0 border-2 border-[#f9b50b] rounded-full translate-x-2 translate-y-2 -z-10 group-hover:translate-x-0 group-hover:translate-y-0 transition-transform duration-500"></div>
                  <img src={s.img} alt={s.name} className="w-full h-full object-cover rounded-full border-4 border-white shadow-lg" />
                </div>
                <h4 className="font-bold text-xl uppercase text-[#221f1f]">{s.name}</h4>
                <p className="text-[#f9b50b] text-xs font-bold uppercase tracking-widest mt-1">{s.role}</p>
              </div>
            ))}
          </div>
        </div> */}

        {/* MAP & VENUE SECTION */}
        <div className="grid lg:grid-cols-2 gap-8 items-stretch">
          <div className="bg-[#221f1f] text-white p-10 rounded-2xl shadow-2xl flex flex-col justify-center" data-aos="fade-right">
            <h3 className="text-2xl font-serif font-bold mb-6 uppercase text-[#f9b50b]">
              Venue Details
            </h3>
            <div className="space-y-4 mb-8">
              <p className="text-xl font-bold">Hanover Park Community Center</p>
              <p className="text-gray-400 text-sm">1919 Walnut Avenue, 3rd Floor, <br />Hanover Park, IL 60133</p>
            </div>

            <div className="border-t border-gray-700 pt-8">
              <h4 className="font-bold mb-4 uppercase text-[#f9b50b] text-sm tracking-widest">Registration</h4>
              <p className="text-gray-400 text-xs leading-relaxed mb-8 italic">
                Secure your spot at our annual gathering. Your presence strengthens our community's interfaith bond.
              </p>
              <Link to="https://forms.gle/SkyzhroLSGkjoacC8" target="_blank" rel="noopener noreferrer" className="block w-full text-center bg-[#f9b50b] text-[#221f1f] py-4 font-bold uppercase tracking-widest hover:bg-white hover:text-[#221f1f] transition-all duration-300 shadow-xl text-xs">
                Register for Event
              </Link>
            </div>
          </div>

          <div className="h-[450px] md:h-auto min-h-[400px] rounded-2xl overflow-hidden shadow-2xl border-4 border-gray-100" data-aos="fade-left">
            <iframe
              title="venue-map"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2966.5414813083323!2d-88.13689722340572!3d41.96719467123233!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x880fa9560f64f89d%3A0xc682946258f338d1!2s1919%20Walnut%20Ave%2C%20Hanover%20Park%2C%20IL%2060133%2C%20USA!5e0!3m2!1sen!2s!4v1700000000000"
              width="100%" height="100%" style={{ border: 0 }} allowFullScreen="" loading="lazy">
            </iframe>
          </div>
        </div>

      </div>
    </div>
  );
};

export default AnnualDinner;